import { Directive, ElementRef, Input } from '@angular/core';

import { BackgroundConfiguration, BackgroundSizeOption, HorizontalPositionOption, ImageModel, VerticalPositionOption } from '../models';
import { TokenApiService } from '../services';
import { CloudImageService } from '../services/cloud-image.service';
import { SharedLibraryService } from '../services/shared-library.service';
import { CropImageDirective } from './crop-image.directive';

@Directive({
    // tslint:disable-next-line: directive-selector
    selector: '[mediaBackground]',
    standalone: false
})
export class MediaBackgroundDirective extends CropImageDirective {
  @Input() public shouldCreateOverlay = true;
  @Input() public overlayUseFullHeight = false;

  @Input() public set mediaBackground(value: BackgroundConfiguration | undefined) {
    this._mediaBackground = value;
    this.initializeElementBackground();
  }

  public get mediaBackground() {
    return this._mediaBackground;
  }

  private mediaQuery = window.matchMedia('(min-width: 768px)');

  private _mediaBackground?: BackgroundConfiguration;

  private imageOverlay: HTMLElement;
  private colorOverlay: HTMLElement;

  constructor(
    protected override readonly sharedLibraryService: SharedLibraryService,
    protected override readonly el: ElementRef,
    protected override readonly tokenApiService: TokenApiService,
    protected override cloudImageService: CloudImageService) {
    super(sharedLibraryService, el, tokenApiService, cloudImageService);
    this.mediaQuery.addEventListener('change', () => this.initializeElementBackground());
  }

  protected initializeElementBackground(): void {
    if (!this._mediaBackground) {
      this.el.nativeElement.style.backgroundColor = null;
      this.el.nativeElement.style.backgroundImage = null;
      this.clearContainerAspectRatio();
      this.cropImage = '';
      this.destroyOverlay();

      return;
    }

    this.setBackgroundImageOverlay();
  }

  private destroyOverlay(): void {
    if (this.imageOverlay) {
      this.imageOverlay.remove();
      this.imageOverlay = null;
    }

    if (this.colorOverlay) {
      this.colorOverlay.remove();
      this.colorOverlay = null;
    }
  }

  private createOverlay(): void {
    if (!this.shouldCreateOverlay || this.imageOverlay) {
      return;
    }

    const imageOverlay = document.createElement('div');
    const colorOverlay = document.createElement('div');
    const padding = this.getContainerVerticalPadding();

    const baseStyle = {
      position: 'absolute',
      width: '100%',
      height: this.overlayUseFullHeight ? '100%' : `calc(100% - ${padding}px)`,
      borderRadius: 'inherit',
      top: '0',
      left: '0',
      zIndex: -1
    };

    Object.assign(imageOverlay.style, baseStyle);
    Object.assign(colorOverlay.style, {
      ...baseStyle,
      zIndex: -2
    });
    this.el.nativeElement.appendChild(colorOverlay);
    this.el.nativeElement.appendChild(imageOverlay);
    this.targetElement = this.imageOverlay = imageOverlay;
    this.colorOverlay = colorOverlay;
  }

  protected setBackgroundImageOverlay(): void {
    this.createOverlay();
    const { backgroundColor } = this._mediaBackground;

    const { mediaBackground, backgroundSize, backgroundPosition } = this.getBackgroundConfiguration();
    const isEmptyArray = Array.isArray(mediaBackground) && !mediaBackground?.length;

    const element = this.getTargetElement();
    const colorElement = this.colorOverlay;

    if (colorElement) {
      colorElement.style.backgroundColor = backgroundColor;
    } else {
      element.style.backgroundColor = backgroundColor;
    }
    
    if (!mediaBackground || isEmptyArray) {
      element.style.backgroundImage = null;
      this.clearContainerAspectRatio();
      this.cropImage = '';

      return;
    }

    if (this.isFile(mediaBackground)) {
      const result = URL.createObjectURL(mediaBackground.file);
      this.cropImage = result;
      this.applyMediaFit(element, backgroundSize || BackgroundSizeOption.COVER);  
      this.applyMediaPosition(element, backgroundPosition);
      return;
    }

    this.additionalOptions = {
      width: this.dynamicWidth ? element.offsetWidth * this.dynamicWidthCoeff : this.imageWidth,
      ...(mediaBackground?.additionalMediaProps ? mediaBackground.additionalMediaProps : {}),
      func: 'bound'
    };

    this.crop = mediaBackground.crop;

    this.applyMediaFit(element, backgroundSize || BackgroundSizeOption.COVER);  
    this.applyMediaPosition(element, backgroundPosition);

    element.style.backgroundRepeat = 'no-repeat';

    this.cropImage = mediaBackground.uri || mediaBackground.url || '';
  }

   protected getBackgroundConfiguration(): Partial<BackgroundConfiguration> {
    if (!this._mediaBackground) {
      return null;
    }
  
    const {
      mediaBackground,
      mobileMediaBackground,
      backgroundSize,
      backgroundPosition,
      mobileBackgroundSize,
      mobileBackgroundPosition,
      useSameBackgroundForBreakpoints
    } = this._mediaBackground;

    if (useSameBackgroundForBreakpoints) {
      return { mediaBackground, backgroundSize, backgroundPosition };
    }

    const isDesktop = this.mediaQuery.matches;

    const desktopConfig = { mediaBackground: mediaBackground, backgroundSize: backgroundSize, backgroundPosition: backgroundPosition };
    const mobileConfig = { mediaBackground: mobileMediaBackground, backgroundSize: mobileBackgroundSize, backgroundPosition: mobileBackgroundPosition };

    const [primary, fallback] = isDesktop ? [desktopConfig, mobileConfig] : [mobileConfig, desktopConfig];

    return primary.mediaBackground ? primary : fallback;
  }

  private applyMediaFit(element: HTMLElement, option: BackgroundSizeOption): void {
    const backgroundSizeMap = {
      [BackgroundSizeOption.AUTO]: BackgroundSizeOption.AUTO,
      [BackgroundSizeOption.CONTAIN]: BackgroundSizeOption.CONTAIN,
      [BackgroundSizeOption.COVER]: BackgroundSizeOption.COVER,
      [BackgroundSizeOption.FULL]: BackgroundSizeOption.COVER
    };

    this.isFullMode = option === BackgroundSizeOption.FULL;
    const isReplacedElement = element instanceof HTMLImageElement

    if (isReplacedElement) {
      element.style.objectFit = backgroundSizeMap[option];
    } else {
      element.style.backgroundSize = backgroundSizeMap[option];
    }
  }

  private applyMediaPosition(element: HTMLElement, backgroundPosition: { vertical: VerticalPositionOption; horizontal: HorizontalPositionOption}): void {
    const isReplacedElement = element instanceof HTMLImageElement
    const { horizontal = HorizontalPositionOption.LEFT, vertical = VerticalPositionOption.TOP } = backgroundPosition || {};
    const position = `${vertical} ${horizontal}`;

    if (isReplacedElement) {
      element.style.objectPosition = position
    } else {
      element.style.backgroundPosition = position;
    }
  }

  protected isFile(mediaImage: Partial<ImageModel> | { file?: File }): boolean {
    return mediaImage && 'file' in mediaImage && mediaImage.file instanceof File;
  }
}
